import { useEffect, useMemo, useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { AccessTime, BeachAccess } from "@mui/icons-material";
import { usePermissionChecker } from "../../../hooks/usePermissionChecker";
import { ServiceModuleTypes } from "../../../types/serviceModuleTypes";
import { timeTrackingApi } from "../../../api/timeTrackingApi";
import { absenceApi } from "../../../api/absenceApi";
import { TimeQuota } from "../../../types/timeTracking";
import { VacationQuota } from "../../../types/absence";
import { EmployeeUserType } from "../../../types/employeeType";
import { PermissionEntityType } from "../../../types/permissionType";
import SampleWidget from "../../dashboard/widgets/GeneralInfo/SampleWidget";
import { ModuleListContainer } from "./EmployeePageWidgets.styles";
import WorkTimeQuotaDialog from "../EmployeesTable/WorkTimeQuotaDialog";
import VacationQuotaDialog from "../EmployeesTable/VacationQuotaDialog";
import { companyManagementApi } from "../../../api/companyManagementApi";

type EmployeeQuotasWidgetProps = {
    employee: EmployeeUserType;
};

const EmployeeQuotasWidget = ({ employee }: EmployeeQuotasWidgetProps) => {
    const { t } = useTranslation();
    const { userHasPermission } = usePermissionChecker();
    const canEditEmployees = userHasPermission(PermissionEntityType.Employees, "Create");
    const timeTrackingServiceModuleId = useMemo(
        () => employee.serviceModules?.find((module) => module.type === ServiceModuleTypes.Timetracking)?.id,
        [employee.serviceModules]
    );
    const absenceServiceModuleId = useMemo(
        () => employee.serviceModules?.find((module) => module.type === ServiceModuleTypes.Absence)?.id,
        [employee.serviceModules]
    );
    const [timeQuotas, setTimeQuotas] = useState<TimeQuota[]>([]);
    const [timeQuotasLoading, setTimeQuotasLoading] = useState(false);
    const [vacationQuotas, setVacationQuotas] = useState<VacationQuota[]>([]);
    const [vacationQuotasLoading, setVacationQuotasLoading] = useState(false);
    const [openWorkTimeDialog, setOpenWorkTimeDialog] = useState(false);
    const [vacationDialogMode, setVacationDialogMode] = useState<"view" | null>(null);
    const [companyId, setCompanyId] = useState<number | null>(null);

    useEffect(() => {
        const fetchCompany = async () => {
            const company = await companyManagementApi.getCompany();
            if (company && typeof company.id === "number") {
                setCompanyId(company.id);
            }
        };
        fetchCompany();
    }, []);

    useEffect(() => {
        const employeeId = employee.id;
        if (!employeeId || !timeTrackingServiceModuleId) return;
        const fetchTimeQuotas = async () => {
            setTimeQuotasLoading(true);
            const result = await timeTrackingApi.getTimeQuotas(
                timeTrackingServiceModuleId,
                employeeId
            );
            if (result.success() && result.result) {
                setTimeQuotas(result.result);
            } else {
                setTimeQuotas([]);
            }
            setTimeQuotasLoading(false);
        };
        fetchTimeQuotas();
    }, [employee.id, timeTrackingServiceModuleId]);

    useEffect(() => {
        const employeeId = employee.id;
        if (!employeeId || !absenceServiceModuleId) return;
        const fetchVacationQuotas = async () => {
            setVacationQuotasLoading(true);
            const result = await absenceApi.getVacationQuotas(
                absenceServiceModuleId,
                employeeId
            );
            if (result.success() && result.result) {
                setVacationQuotas(result.result);
            } else {
                setVacationQuotas([]);
            }
            setVacationQuotasLoading(false);
        };
        fetchVacationQuotas();
    }, [employee.id, absenceServiceModuleId]);

    const timeQuotaRanges = useMemo(() => {
        if (!timeQuotas.length) return [];
        const currentYear = dayjs().year();
        const sorted = timeQuotas
            .filter((quota) => quota.year === currentYear)
            .sort((a, b) => {
                if (a.year !== b.year) return a.year - b.year;
                return a.calendarWeek - b.calendarWeek;
            });

        if (sorted.length === 0) return [];

        const ranges: Array<{
            startYear: number;
            startWeek: number;
            endYear: number;
            endWeek: number;
            plannedWorkMinutes: number;
        }> = [];

        let current = {
            startYear: sorted[0].year,
            startWeek: sorted[0].calendarWeek,
            endYear: sorted[0].year,
            endWeek: sorted[0].calendarWeek,
            plannedWorkMinutes: sorted[0].plannedWorkMinutes,
        };

        for (let i = 1; i < sorted.length; i++) {
            const quota = sorted[i];
            const isSameHours = quota.plannedWorkMinutes === current.plannedWorkMinutes;
            const isNextWeekSameYear =
                quota.year === current.endYear && quota.calendarWeek === current.endWeek + 1;

            if (isSameHours && isNextWeekSameYear) {
                current.endYear = quota.year;
                current.endWeek = quota.calendarWeek;
            } else {
                ranges.push(current);
                current = {
                    startYear: quota.year,
                    startWeek: quota.calendarWeek,
                    endYear: quota.year,
                    endWeek: quota.calendarWeek,
                    plannedWorkMinutes: quota.plannedWorkMinutes,
                };
            }
        }

        ranges.push(current);
        return ranges;
    }, [timeQuotas]);

    const toHours = (minutes: number) => Math.round((minutes / 60) * 100) / 100;
    const currentYear = dayjs().year();
    const vacationQuotaItems = useMemo(() => {
        return vacationQuotas.filter((quota) =>
            dayjs(quota.validFrom).year() === currentYear ||
            dayjs(quota.validUntil).year() === currentYear
        );
    }, [vacationQuotas, currentYear]);

    return (
        <SampleWidget
            title={t("employeesPage.singleEmployee.quotas.title")}
            loading={timeQuotasLoading || vacationQuotasLoading}
            hasData
        >
            <ModuleListContainer>
                {!timeQuotaRanges.length && !vacationQuotaItems.length ? (
                    <Typography variant="body2" color="textSecondary">
                        {t("employeesPage.singleEmployee.quotas.empty")}
                    </Typography>
                ) : (
                    <>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="subtitle2">
                                {t("employeesPage.singleEmployee.timeQuotas.title")}
                            </Typography>
                            <Tooltip title={t("employeesPage.workTimeQuota.title")}>
                                <IconButton size="small" onClick={() => setOpenWorkTimeDialog(true)}>
                                    <AccessTime fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        {timeQuotaRanges.length > 0 ? (
                            timeQuotaRanges.map((range, index) => (
                                <Box key={`${range.startWeek}-${range.endWeek}-${index}`}>
                                    {range.startWeek === range.endWeek &&
                                        range.startYear === range.endYear ? (
                                        <Typography variant="body2">
                                            {t("employeesPage.singleEmployee.timeQuotas.rangeSingle", {
                                                week: range.startWeek,
                                                year: range.startYear,
                                                hours: toHours(range.plannedWorkMinutes),
                                            })}
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2">
                                            {t("employeesPage.singleEmployee.timeQuotas.range", {
                                                startWeek: range.startWeek,
                                                startYear: range.startYear,
                                                endWeek: range.endWeek,
                                                endYear: range.endYear,
                                                hours: toHours(range.plannedWorkMinutes),
                                            })}
                                        </Typography>
                                    )}
                                </Box>
                            ))
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                {t("employeesPage.singleEmployee.timeQuotas.empty")}
                            </Typography>
                        )}

                        <Box display="flex" alignItems="center" gap={1} mt={1}>
                            <Typography variant="subtitle2">
                                {t("employeesPage.singleEmployee.vacationQuotas.title")}
                            </Typography>
                            <Tooltip title={t("employeesPage.vacationQuota.viewTooltip")}>
                                <IconButton size="small" onClick={() => setVacationDialogMode("view")}>
                                    <BeachAccess fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        {vacationQuotaItems.length > 0 ? (
                            vacationQuotaItems.map((quota) => (
                                <Box key={`${quota.id}-${quota.validFrom}`}>
                                    <Typography variant="body2">
                                        {t("employeesPage.singleEmployee.vacationQuotas.range", {
                                            from: dayjs(quota.validFrom).format("DD.MM.YYYY"),
                                            until: dayjs(quota.validUntil).format("DD.MM.YYYY"),
                                            days: quota.totalVacationDays,
                                        })}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {t("employeesPage.singleEmployee.vacationQuotas.surplus", {
                                            days: quota.surplusFromPrevious,
                                            until: dayjs(quota.surplusValidUntil).format("DD.MM.YYYY"),
                                        })}
                                    </Typography>
                                </Box>
                            ))
                        ) : (
                            <Typography variant="body2" color="textSecondary">
                                {t("employeesPage.singleEmployee.vacationQuotas.empty")}
                            </Typography>
                        )}
                    </>
                )}
            </ModuleListContainer>
            <WorkTimeQuotaDialog
                open={openWorkTimeDialog}
                onClose={() => setOpenWorkTimeDialog(false)}
                employee={employee}
                timeTrackingServiceModuleId={timeTrackingServiceModuleId}
                canEdit={canEditEmployees}
                companyId={companyId}
            />
            <VacationQuotaDialog
                open={vacationDialogMode !== null}
                onClose={() => setVacationDialogMode(null)}
                employee={employee}
                absenceServiceModuleId={absenceServiceModuleId}
                canEdit={canEditEmployees}
                companyId={companyId}
                mode={vacationDialogMode ?? "view"}
            />
        </SampleWidget>
    );
};

export default EmployeeQuotasWidget;
